import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, ArchiveRestore, Building2, CalendarClock, Container, Pencil, Plus, Truck, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import { PageHeader, usePermission, useTenant } from "@/components/AppShell";
import { COUNTRIES, EMPTY, Field, LineRow, ProductPicker, type Line } from "@/components/ManualOrderSheet";
import { Pager } from "@/components/Pager";
import { Badge, Button, Card, cx, EmptyState, Input, Skeleton } from "@/components/ui";
import { api, ApiError, type B2bCustomer, type B2bCustomerInput, type B2bLoadCarrier, type B2bShipping } from "@/lib/api";
import { countryName, fmtNumber, fmtRelative } from "@/lib/format";
import { STATUS } from "./Orders";
import { deliveryShort, LOAD_CARRIER_LABEL, requirementTexts, SHIPPING_LABEL } from "@/components/B2bInfo";

import { t, msg } from "@/lib/i18n";
/** B2B-Lieferungen: Lieferungen an Händler/Zentrallager und deren Stammdaten */
export function B2bPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "customers" ? "customers" : "orders";
  const mayEdit = usePermission()("orders");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<B2bCustomer | "new" | null>(null);
  const navigate = useNavigate();

  return (
    <div className="pb-24">
      <PageHeader
        title={t("B2B-Lieferungen")}
        icon={<Container className="size-5" />}
        description={t("Ware an Händler und Zentrallager – mit Liefertermin, Vorgaben des Händlers und Lieferschein mit seiner Bestellnummer. Geht automatisch ans Lager.")}
        actions={
          mayEdit ? (
            tab === "orders" ? (
              <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>{t("Lieferung anlegen")}</Button>
            ) : (
              <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setEditing("new")}>{t("Abnehmer anlegen")}</Button>
            )
          ) : undefined
        }
      />
      <div className="px-4 md:px-8">
        <div className="mb-4 flex w-fit rounded-lg border border-border p-0.5 bg-surface">
          {(["orders", "customers"] as const).map((k) => (
            <button key={k} onClick={() => setParams(k === "orders" ? {} : { tab: k })} className={cx("h-8 px-3.5 rounded-md text-[13px] font-medium", tab === k ? "bg-ink text-ink-fg" : "text-fg-muted hover:text-fg")}>
              {k === "orders" ? t("Lieferungen") : t("Abnehmer")}
            </button>
          ))}
        </div>
        {tab === "orders" ? <Deliveries onCreate={mayEdit ? () => setCreating(true) : undefined} /> : <Customers onEdit={mayEdit ? setEditing : undefined} />}
      </div>
      <B2bOrderSheet open={creating} onOpenChange={setCreating} onNewCustomer={() => { setCreating(false); setEditing("new"); }} onCreated={(id) => navigate(`/orders?open=${id}`)} />
      <CustomerSheet customer={editing} onClose={() => setEditing(null)} />
    </div>
  );
}

function Deliveries({ onCreate }: { onCreate?: () => void }) {
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const q = useQuery({ queryKey: ["b2b-orders", tenantId, page], queryFn: () => api.b2b.orders(tenantId, { page, pageSize: 50 }) });
  if (q.isLoading) return <Skeleton className="h-48 w-full" />;
  const d = q.data;
  if (!d?.items.length)
    return (
      <Card>
        <EmptyState icon={<Container className="size-5" />} title={t("Noch keine B2B-Lieferungen")} action={onCreate ? <Button variant="primary" icon={<Plus className="size-4" />} onClick={onCreate}>{t("Erste Lieferung anlegen")}</Button> : undefined}>
          {t("Lieferungen an Händler werden hier mit Bestellnummer, Liefertermin und Status geführt.")}
        </EmptyState>
      </Card>
    );
  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="text-[12px] text-fg-subtle bg-surface-2/60">
            <tr>
              <th className="text-left font-medium px-4 py-2">{t("Lieferung")}</th>
              <th className="text-left font-medium px-3 py-2">{t("Abnehmer")}</th>
              <th className="text-left font-medium px-3 py-2">{t("Bestell-Nr.")}</th>
              <th className="text-left font-medium px-3 py-2">{t("Liefertermin")}</th>
              <th className="text-left font-medium px-3 py-2">{t("Versand")}</th>
              <th className="text-right font-medium px-3 py-2">{t("Stück")}</th>
              <th className="text-left font-medium px-4 py-2">{t("Status")}</th>
            </tr>
          </thead>
          <tbody>
            {d.items.map((o) => {
              const s = STATUS[o.status];
              return (
                <tr key={o.id} onClick={() => navigate(`/orders?open=${o.id}`)} className="border-t border-border hover:bg-surface-2/50 cursor-pointer">
                  <td className="px-4 py-2.5"><div className="font-medium tabular-nums whitespace-nowrap">{o.orderNumber}</div><div className="text-[12px] text-fg-subtle whitespace-nowrap">{fmtRelative(o.placedAt)}</div></td>
                  <td className="px-3 py-2.5 max-w-[220px] truncate">{o.b2b.customerName}</td>
                  <td className="px-3 py-2.5 font-mono text-[12.5px] whitespace-nowrap">{o.b2b.purchaseOrderNumber}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">{deliveryShort(o.b2b) ?? <span className="text-fg-subtle">–</span>}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">{SHIPPING_LABEL[o.b2b.shipping]} · {LOAD_CARRIER_LABEL[o.b2b.loadCarrier]}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{fmtNumber(o.units)}</td>
                  <td className="px-4 py-2.5"><Badge tone={s.tone}>{t(s.label)}</Badge></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pager total={d.total} page={d.page} pageSize={d.pageSize} onPage={setPage} />
    </Card>
  );
}

function Customers({ onEdit }: { onEdit?: (c: B2bCustomer) => void }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const [archived, setArchived] = useState(false);
  const q = useQuery({ queryKey: ["b2b-customers", tenantId, archived], queryFn: () => api.b2b.customers(tenantId, archived) });
  const archive = useMutation({
    mutationFn: ({ id, on }: { id: string; on: boolean }) => api.b2b.archiveCustomer(tenantId, id, on),
    onSuccess: (_r, v) => {
      toast.success(v.on ? "Archiviert" : "Wiederhergestellt");
      void qc.invalidateQueries({ queryKey: ["b2b-customers", tenantId] });
    },
  });
  return (
    <>
      <label className="mb-3 inline-flex items-center gap-2 text-[12.5px] text-fg-muted">
        <input type="checkbox" className="accent-ink" checked={archived} onChange={(e) => setArchived(e.target.checked)} /> {t("Archivierte zeigen")}
      </label>
      {q.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : !q.data?.length ? (
        <Card>
          <EmptyState icon={<Building2 className="size-5" />} title={archived ? t("Keine archivierten Abnehmer") : t("Noch keine Abnehmer")}>
            {t("Händler und Zentrallager einmal anlegen – mit Warenannahme, Voranmeldung und Vorgaben. Jede Lieferung übernimmt das automatisch.")}
          </EmptyState>
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {q.data.map((c) => {
            const req = requirementTexts(c.requirements);
            return (
              <Card key={c.id} className="p-4">
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-[14px] truncate">{c.name}</p>
                    <p className="text-[12.5px] text-fg-muted truncate">
                      {[c.address.street, c.address.houseNumber].filter(Boolean).join(" ")} · {c.address.zip} {c.address.city}{c.address.country !== "DE" ? ` · ${countryName(c.address.country)}` : ""}
                    </p>
                  </div>
                  {onEdit && !c.archivedAt && <Button size="sm" variant="ghost" icon={<Pencil className="size-3.5" />} onClick={() => onEdit(c)}>{t("Bearbeiten")}</Button>}
                  {onEdit && <Button size="sm" variant="ghost" icon={c.archivedAt ? <ArchiveRestore className="size-3.5" /> : <Archive className="size-3.5" />} loading={archive.isPending && archive.variables?.id === c.id} onClick={() => archive.mutate({ id: c.id, on: !c.archivedAt })} aria-label={c.archivedAt ? t("Wiederherstellen") : t("Archivieren")} />}
                </div>
                <div className="mt-2.5 flex flex-wrap gap-1.5">
                  <Badge icon={<Truck className="size-3" />}>{SHIPPING_LABEL[c.defaultShipping]} · {LOAD_CARRIER_LABEL[c.defaultLoadCarrier]}</Badge>
                  {c.gln && <Badge>{t("GLN")} {c.gln}</Badge>}
                  {c.avisRequired && <Badge tone="info" icon={<CalendarClock className="size-3" />}>{t("Voranmeldung")}</Badge>}
                  {req.map((r) => <Badge key={r} tone="warning">{r}</Badge>)}
                </div>
                <p className="mt-2.5 text-[12px] text-fg-subtle">
                  {c.receivingHours ? t("Warenannahme {receivingHours} · ", { receivingHours: c.receivingHours }) : ""}
                  {c.deliveries ? `${fmtNumber(c.deliveries)} ${c.deliveries === 1 ? "Lieferung" : "Lieferungen"}${c.lastDeliveryAt ? `, zuletzt ${fmtRelative(c.lastDeliveryAt)}` : ""}` : t("noch keine Lieferung")}
                </p>
              </Card>
            );
          })}
        </div>
      )}
    </>
  );
}

const emptyCustomer = (): B2bCustomerInput => ({
  name: "",
  customerNumber: null,
  gln: null,
  address: { ...EMPTY, lastName: "Wareneingang" },
  receivingHours: null,
  avisRequired: false,
  avisContact: null,
  defaultShipping: "freight",
  defaultLoadCarrier: "pallet",
  requirements: { minShelfLifeDays: null, fullCartonsOnly: false, singleBatch: false },
  warehouseNote: null,
});

/** GLN prüfen wie der Server (13 Ziffern, Prüfziffer) – Fehler schon beim Tippen zeigen */
const glnOk = (v: string) => {
  if (!/^\d{13}$/.test(v)) return false;
  const d = v.split("").map(Number);
  return (10 - (d.slice(0, 12).reduce((s, x, i) => s + x * (i % 2 ? 3 : 1), 0) % 10)) % 10 === d[12];
};

function CustomerSheet({ customer, onClose }: { customer: B2bCustomer | "new" | null; onClose: () => void }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const [f, setF] = useState<B2bCustomerInput>(emptyCustomer);
  const [tried, setTried] = useState(false);
  useEffect(() => {
    if (customer === "new") setF(emptyCustomer());
    else if (customer) setF({ ...customer, address: { ...EMPTY, ...customer.address } });
    setTried(false);
  }, [customer]);
  const save = useMutation({
    mutationFn: (x: B2bCustomerInput) => (customer && customer !== "new" ? api.b2b.updateCustomer(tenantId, customer.id, x) : api.b2b.createCustomer(tenantId, x)),
    onSuccess: (c) => {
      toast.success(customer === "new" ? `${c.name} angelegt` : "Gespeichert");
      void qc.invalidateQueries({ queryKey: ["b2b-customers", tenantId] });
      onClose();
    },
    onError: (e) => toast.error("Speichern fehlgeschlagen", e instanceof ApiError ? { description: (e.body as { message?: string })?.message } : undefined),
  });
  const set = <K extends keyof B2bCustomerInput>(k: K, v: B2bCustomerInput[K]) => setF((x) => ({ ...x, [k]: v }));
  const setA = (k: keyof B2bCustomerInput["address"], v: string) => setF((x) => ({ ...x, address: { ...x.address, [k]: v } }));
  const errors = {
    name: !f.name.trim() ? "Name fehlt" : undefined,
    street: !f.address.street.trim() ? "Straße fehlt" : undefined,
    zip: !f.address.zip.trim() ? "PLZ fehlt" : undefined,
    city: !f.address.city.trim() ? "Ort fehlt" : undefined,
    lastName: !f.address.lastName.trim() ? "Empfänger fehlt" : undefined,
    gln: f.gln && !glnOk(f.gln) ? "GLN ungültig (13 Ziffern mit Prüfziffer)" : undefined,
  };
  const err = (k: keyof typeof errors) => (tried ? errors[k] : k === "gln" ? errors.gln : undefined);
  const valid = Object.values(errors).every((e) => !e);
  const submit = () => {
    setTried(true);
    if (!valid) return;
    const clean = (v: string | null) => (v && v.trim() ? v.trim() : null);
    save.mutate({ ...f, customerNumber: clean(f.customerNumber), gln: clean(f.gln), receivingHours: clean(f.receivingHours), avisContact: clean(f.avisContact), warehouseNote: clean(f.warehouseNote), address: { ...f.address, company: f.address.company || f.name } });
  };

  return (
    <Dialog.Root open={!!customer} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/20 animate-fade-in" />
        <Dialog.Content onOpenAutoFocus={(e) => e.preventDefault()} className="fixed z-50 right-0 top-0 h-full w-[min(640px,100vw)] bg-surface border-l border-border shadow-2xl flex flex-col animate-sheet-in outline-none">
          <div className="px-6 pt-5 pb-4 border-b border-border flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-[17px] font-semibold flex items-center gap-2"><Building2 className="size-4 text-fg-subtle" /> {customer === "new" ? t("Abnehmer anlegen") : t("Abnehmer bearbeiten")}</Dialog.Title>
              <Dialog.Description className="text-[13px] text-fg-muted mt-0.5">{t("Händler, Zentrallager oder Filiale. Änderungen gelten für neue Lieferungen – bereits angelegte bleiben unverändert.")}</Dialog.Description>
            </div>
            <Dialog.Close className="size-8 -mr-2 grid place-items-center rounded-lg text-fg-subtle hover:bg-surface-2 hover:text-fg" aria-label={t("Schließen")}><X className="size-4" /></Dialog.Close>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-7">
            <section className="grid grid-cols-6 gap-x-3 gap-y-2.5">
              <Field label={t("Name")} error={err("name")} className="col-span-6"><Input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder={t("z. B. Rossmann Zentrallager Burgwedel")} invalid={!!err("name")} /></Field>
              <Field label={t("Kunden-Nr. (optional)")} className="col-span-3"><Input value={f.customerNumber ?? ""} onChange={(e) => set("customerNumber", e.target.value)} placeholder={t("eure Nummer für den Händler")} /></Field>
              <Field label={t("GLN (optional)")} error={err("gln")} className="col-span-3"><Input value={f.gln ?? ""} onChange={(e) => set("gln", e.target.value.replace(/\D/g, "").slice(0, 13))} inputMode="numeric" placeholder={t("13 Ziffern")} invalid={!!err("gln")} /></Field>
            </section>
            <section>
              <h3 className="text-[12px] font-semibold text-fg-muted uppercase tracking-wide">{t("Lieferadresse")}</h3>
              <div className="mt-2 grid grid-cols-6 gap-x-3 gap-y-2.5">
                <Field label={t("Firma")} className="col-span-6"><Input value={f.address.company ?? ""} onChange={(e) => setA("company", e.target.value)} placeholder={f.name || t("wie Name")} /></Field>
                <Field label={t("Empfänger / Abteilung")} error={err("lastName")} className="col-span-6"><Input value={f.address.lastName} onChange={(e) => setA("lastName", e.target.value)} invalid={!!err("lastName")} /></Field>
                <Field label={t("Straße")} error={err("street")} className="col-span-4"><Input value={f.address.street} onChange={(e) => setA("street", e.target.value)} invalid={!!err("street")} /></Field>
                <Field label={t("Nr.")} className="col-span-2"><Input value={f.address.houseNumber ?? ""} onChange={(e) => setA("houseNumber", e.target.value)} /></Field>
                <Field label={t("Zusatz (z. B. Tor/Rampe)")} className="col-span-6"><Input value={f.address.addition ?? ""} onChange={(e) => setA("addition", e.target.value)} /></Field>
                <Field label={t("PLZ")} error={err("zip")} className="col-span-2"><Input value={f.address.zip} onChange={(e) => setA("zip", e.target.value)} invalid={!!err("zip")} /></Field>
                <Field label={t("Ort")} error={err("city")} className="col-span-2"><Input value={f.address.city} onChange={(e) => setA("city", e.target.value)} invalid={!!err("city")} /></Field>
                <Field label={t("Land")} className="col-span-2">
                  <select value={f.address.country} onChange={(e) => setA("country", e.target.value)} className="h-9 w-full rounded-lg border border-border bg-surface px-2.5 text-[13px]">
                    {COUNTRIES.map((c) => <option key={c} value={c}>{countryName(c)}</option>)}
                  </select>
                </Field>
              </div>
            </section>
            <section>
              <h3 className="text-[12px] font-semibold text-fg-muted uppercase tracking-wide">{t("Anlieferung")}</h3>
              <div className="mt-2 grid grid-cols-6 gap-x-3 gap-y-2.5">
                <Field label={t("Warenannahme")} className="col-span-6"><Input value={f.receivingHours ?? ""} onChange={(e) => set("receivingHours", e.target.value)} placeholder={t("z. B. Mo–Fr 6–14 Uhr")} /></Field>
                <Field label={t("Versand üblicherweise")} className="col-span-3">
                  <Segmented value={f.defaultShipping} options={[["freight", msg("Spedition")], ["parcel", msg("Paket")]]} onChange={(v) => set("defaultShipping", v as B2bShipping)} />
                </Field>
                <Field label={t("auf")} className="col-span-3">
                  <Segmented value={f.defaultLoadCarrier} options={[["pallet", msg("Palette")], ["carton", msg("Karton")]]} onChange={(v) => set("defaultLoadCarrier", v as B2bLoadCarrier)} />
                </Field>
              </div>
              <label className="mt-3 flex items-center gap-2 text-[13px] font-medium">
                <input type="checkbox" className="accent-ink" checked={f.avisRequired} onChange={(e) => set("avisRequired", e.target.checked)} /> {t("Anlieferung muss vorab angemeldet werden (Avis / Zeitfenster)")}
              </label>
              {f.avisRequired && <Field label={t("Ansprechpartner für die Anmeldung")} className="mt-2"><Input value={f.avisContact ?? ""} onChange={(e) => set("avisContact", e.target.value)} placeholder={t("Name, Telefon, E-Mail oder Buchungsportal")} /></Field>}
            </section>
            <section>
              <h3 className="text-[12px] font-semibold text-fg-muted uppercase tracking-wide">{t("Vorgaben des Händlers")}</h3>
              <div className="mt-2 space-y-2.5 text-[13px]">
                <label className="flex items-center gap-2">
                  <input type="checkbox" className="accent-ink" checked={f.requirements.minShelfLifeDays != null} onChange={(e) => set("requirements", { ...f.requirements, minShelfLifeDays: e.target.checked ? 180 : null })} />
                  {t("Mindesthaltbarkeit bei Ankunft")}
                  {f.requirements.minShelfLifeDays != null && (
                    <span className="inline-flex items-center gap-1.5 text-fg-muted">
                      {t("mind.")}
                      <Input value={String(f.requirements.minShelfLifeDays)} inputMode="numeric" className="w-20 [&_input]:text-right" onChange={(e) => set("requirements", { ...f.requirements, minShelfLifeDays: Math.max(1, Math.min(2000, Number(e.target.value.replace(/\D/g, "")) || 1)) })} />
                      {t("Tage")}
                    </span>
                  )}
                </label>
                <label className="flex items-center gap-2"><input type="checkbox" className="accent-ink" checked={f.requirements.fullCartonsOnly} onChange={(e) => set("requirements", { ...f.requirements, fullCartonsOnly: e.target.checked })} /> {t("nur ganze Kartons (Verpackungseinheiten)")}</label>
                <label className="flex items-center gap-2"><input type="checkbox" className="accent-ink" checked={f.requirements.singleBatch} onChange={(e) => set("requirements", { ...f.requirements, singleBatch: e.target.checked })} /> {t("je Artikel nur eine Charge")}</label>
              </div>
              <p className="mt-2 text-[12px] text-fg-subtle">{t("Gehen mit jeder Lieferung als Hinweis ans Lager und stehen auf dem Lieferschein. Die automatische Prüfung gegen Bestand und Chargen folgt im nächsten Schritt.")}</p>
            </section>
            <Field label={t("Dauerhafter Hinweis fürs Lager (optional)")}><textarea value={f.warehouseNote ?? ""} onChange={(e) => set("warehouseNote", e.target.value.slice(0, 1000))} rows={2} className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-[13px] outline-none focus:border-border-strong" placeholder={t("z. B. Paletten max. 1,60 m, Euro-Paletten tauschen")} /></Field>
          </div>
          <div className="px-6 py-3.5 border-t border-border flex items-center justify-end gap-3">
            {tried && !valid && <span className="mr-auto text-[12.5px] text-danger">{t("Bitte die markierten Felder ausfüllen.")}</span>}
            <Dialog.Close asChild><Button variant="ghost">{t("Abbrechen")}</Button></Dialog.Close>
            <Button variant="primary" loading={save.isPending} onClick={submit}>{customer === "new" ? t("Abnehmer anlegen") : t("Speichern")}</Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Segmented({ value, options, onChange }: { value: string; options: [string, string][]; onChange: (v: string) => void }) {
  return (
    <div className="inline-flex w-full rounded-lg border border-border p-0.5">
      {options.map(([v, l]) => (
        <button key={v} type="button" onClick={() => onChange(v)} aria-pressed={value === v} className={cx("flex-1 h-8 rounded-md text-[12.5px] font-medium", value === v ? "bg-ink text-ink-fg" : "text-fg-muted hover:text-fg")}>{l}</button>
      ))}
    </div>
  );
}

const todayBerlin = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Berlin" });

/** Neue B2B-Lieferung: Abnehmer wählen, Bestellnummer, Termin, Positionen */
function B2bOrderSheet({ open, onOpenChange, onCreated, onNewCustomer }: { open: boolean; onOpenChange: (o: boolean) => void; onCreated: (id: string) => void; onNewCustomer: () => void }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const customers = useQuery({ queryKey: ["b2b-customers", tenantId, false], queryFn: () => api.b2b.customers(tenantId), enabled: open });
  const methods = useQuery({ queryKey: ["shipping-methods", tenantId], queryFn: () => api.backend.shippingMethods(tenantId), enabled: open });
  const activeMethods = methods.data?.warehouses.find((w) => w.active)?.shippingMethods ?? [];
  const [customerId, setCustomerId] = useState("");
  const [po, setPo] = useState("");
  const [date, setDate] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [shipping, setShipping] = useState<B2bShipping>("freight");
  const [carrier, setCarrier] = useState<B2bLoadCarrier>("pallet");
  const [methodId, setMethodId] = useState("");
  const [lines, setLines] = useState<Line[]>([]);
  const [note, setNote] = useState("");
  const [reference, setReference] = useState("");
  const [tried, setTried] = useState(false);
  const customer = useMemo(() => customers.data?.find((c) => c.id === customerId) ?? null, [customers.data, customerId]);

  useEffect(() => {
    if (!open) return;
    setCustomerId(""); setPo(""); setDate(""); setFrom(""); setTo(""); setMethodId(""); setLines([]); setNote(""); setReference(""); setTried(false);
  }, [open]);
  useEffect(() => {
    if (customer) { setShipping(customer.defaultShipping); setCarrier(customer.defaultLoadCarrier); }
  }, [customer]);

  const create = useMutation({
    mutationFn: () =>
      api.b2b.createOrder(tenantId, {
        customerId, purchaseOrderNumber: po.trim(), deliveryDate: date || null, windowFrom: date && from ? from : null, windowTo: date && to ? to : null,
        shipping, loadCarrier: carrier, lines: lines.map((l) => ({ productId: l.product.id, quantity: l.quantity, ...(l.batch ? { batch: l.batch } : {}) })),
        ...(methodId ? { shippingMethodId: methodId } : {}), ...(note.trim() ? { note: note.trim() } : {}), ...(reference.trim() ? { reference: reference.trim() } : {}),
      }),
    onSuccess: (r) => {
      toast.success(`Lieferung ${r.orderNumber} angelegt`, { description: msg("Wird geprüft und mit Lieferschein ans Lager übergeben.") });
      void qc.invalidateQueries({ queryKey: ["b2b-orders", tenantId] });
      void qc.invalidateQueries({ queryKey: ["orders-search", tenantId] });
      onOpenChange(false);
      onCreated(r.id);
    },
    onError: (e) => toast.error("Nicht angelegt", e instanceof ApiError ? { description: (e.body as { message?: string })?.message } : undefined),
  });

  const errors = {
    customer: !customerId ? "Abnehmer wählen" : undefined,
    po: !po.trim() ? "Bestellnummer des Händlers fehlt" : undefined,
    date: date && date < todayBerlin() ? "Termin liegt in der Vergangenheit" : undefined,
    window: from && to && from >= to ? "„von“ muss vor „bis“ liegen" : undefined,
    lines: !lines.length ? "Mindestens ein Artikel" : undefined,
  };
  const valid = Object.values(errors).every((e) => !e);
  const err = (k: keyof typeof errors) => (tried || k === "date" || k === "window" ? errors[k] : undefined);
  const units = lines.reduce((s, l) => s + l.quantity, 0);
  const req = customer ? requirementTexts(customer.requirements) : [];

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/20 animate-fade-in" />
        <Dialog.Content onOpenAutoFocus={(e) => e.preventDefault()} onEscapeKeyDown={(e) => { if ((document.activeElement as HTMLElement | null)?.closest("[data-picker=open]")) e.preventDefault(); }} className="fixed z-50 right-0 top-0 h-full w-[min(680px,100vw)] bg-surface border-l border-border shadow-2xl flex flex-col animate-sheet-in outline-none">
          <div className="px-6 pt-5 pb-4 border-b border-border flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-[17px] font-semibold flex items-center gap-2"><Container className="size-4 text-fg-subtle" /> {t("B2B-Lieferung anlegen")}</Dialog.Title>
              <Dialog.Description className="text-[13px] text-fg-muted mt-0.5">{t("Geht wie jeder Auftrag durch Prüfung und Bestandsvergabe – ans Lager mit Liefertermin, Hinweisen und Lieferschein.")}</Dialog.Description>
            </div>
            <Dialog.Close className="size-8 -mr-2 grid place-items-center rounded-lg text-fg-subtle hover:bg-surface-2 hover:text-fg" aria-label={t("Schließen")}><X className="size-4" /></Dialog.Close>
          </div>
          <div data-sheet-body className="flex-1 overflow-y-auto px-6 py-5 space-y-7">
            <section>
              <h3 className="text-[12px] font-semibold text-fg-muted uppercase tracking-wide">{t("Abnehmer")}</h3>
              {customers.data && !customers.data.length ? (
                <div className="mt-2 rounded-xl border border-dashed border-border p-4 text-[13px] text-fg-muted">
                  {t("Noch kein Abnehmer angelegt.")} <button className="font-medium text-fg underline underline-offset-2" onClick={onNewCustomer}>{t("Jetzt anlegen")}</button>
                </div>
              ) : (
                <div className="mt-2 flex gap-2">
                  <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} className={cx("h-9 flex-1 min-w-0 rounded-lg border bg-surface px-2.5 text-[13px]", err("customer") ? "border-danger" : "border-border")}>
                    <option value="">{t("Abnehmer wählen …")}</option>
                    {customers.data?.map((c) => <option key={c.id} value={c.id}>{c.name} – {c.address.zip} {c.address.city}</option>)}
                  </select>
                  <Button variant="ghost" icon={<Plus className="size-4" />} onClick={onNewCustomer}>{t("Neu")}</Button>
                </div>
              )}
              {err("customer") && <p className="mt-1 text-[11.5px] text-danger">{err("customer")}</p>}
              {customer && (
                <div className="mt-2.5 rounded-xl bg-surface-2/70 px-3.5 py-2.5 text-[12.5px] text-fg-muted space-y-0.5">
                  <p className="text-fg">{[customer.address.company, customer.address.lastName].filter(Boolean).join(" · ")}, {[customer.address.street, customer.address.houseNumber].filter(Boolean).join(" ")}, {customer.address.zip} {customer.address.city}</p>
                  {customer.receivingHours && <p>{t("Warenannahme")} {customer.receivingHours}</p>}
                  {customer.avisRequired && <p>{t("Anlieferung wird vorab angemeldet")}{customer.avisContact ? ` – ${customer.avisContact}` : ""}</p>}
                  {req.length > 0 && <p className="text-warning">{t("Vorgaben:")} {req.join(" · ")}</p>}
                </div>
              )}
            </section>

            <section className="grid grid-cols-6 gap-x-3 gap-y-2.5">
              <Field label={t("Bestell-Nr. des Händlers")} error={err("po")} className="col-span-6 sm:col-span-3"><Input value={po} onChange={(e) => setPo(e.target.value.slice(0, 60))} placeholder={t("steht auf Lieferschein und Packstücken")} invalid={!!err("po")} /></Field>
              <Field label={t("Liefertermin beim Händler (optional)")} error={err("date")} className="col-span-6 sm:col-span-3"><Input type="date" value={date} min={todayBerlin()} onChange={(e) => setDate(e.target.value)} invalid={!!err("date")} /></Field>
              {date && (
                <>
                  <Field label={t("Zeitfenster von")} className="col-span-3 sm:col-span-2"><Input type="time" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
                  <Field label={t("bis")} error={err("window")} className="col-span-3 sm:col-span-2"><Input type="time" value={to} onChange={(e) => setTo(e.target.value)} invalid={!!err("window")} /></Field>
                </>
              )}
            </section>

            <section className="grid grid-cols-6 gap-x-3 gap-y-2.5">
              <Field label={t("Versand")} className="col-span-6 sm:col-span-3"><Segmented value={shipping} options={[["freight", msg("Spedition")], ["parcel", msg("Paket")]]} onChange={(v) => setShipping(v as B2bShipping)} /></Field>
              <Field label={t("auf")} className="col-span-6 sm:col-span-3"><Segmented value={carrier} options={[["pallet", msg("Palette")], ["carton", msg("Karton")]]} onChange={(v) => setCarrier(v as B2bLoadCarrier)} /></Field>
              <Field label={t("Versandart im Lager")} className="col-span-6">
                <select value={methodId} onChange={(e) => setMethodId(e.target.value)} className="h-9 w-full rounded-lg border border-border bg-surface px-2.5 text-[13px]">
                  <option value="">{t("Automatisch (nach Versandregeln)")}</option>
                  {activeMethods.map((m) => <option key={m.id} value={m.id}>{m.name}{m.carrier ? ` (${m.carrier})` : ""}</option>)}
                </select>
              </Field>
              {shipping === "freight" && !methodId && <p className="col-span-6 -mt-1 text-[12px] text-fg-subtle">{t("Für Spedition am besten die Speditions-Versandart des Lagers wählen – sonst plant Logiful den Transport anhand des Hinweises.")}</p>}
            </section>

            <section>
              <h3 className="text-[12px] font-semibold text-fg-muted uppercase tracking-wide">{t("Positionen")}</h3>
              <ProductPicker open={open} exclude={lines.map((l) => l.product.id)} onPick={(p) => setLines((xs) => [...xs, { product: p, quantity: 1 }])} />
              {lines.length > 0 && (
                <ul className="mt-2 rounded-xl border border-border divide-y divide-border">
                  {lines.map((l) => (
                    <LineRow key={l.product.id} line={l} onQty={(q) => setLines((xs) => xs.map((x) => (x.product.id === l.product.id ? { ...x, quantity: q } : x)))} onRemove={() => setLines((xs) => xs.filter((x) => x.product.id !== l.product.id))} onBatch={(b) => setLines((xs) => xs.map((x) => (x.product.id === l.product.id ? { ...x, batch: b } : x)))} />
                  ))}
                </ul>
              )}
              {err("lines") && <p className="mt-1 text-[11.5px] text-danger">{err("lines")}</p>}
            </section>

            <section className="grid grid-cols-6 gap-x-3 gap-y-2.5">
              <Field label={t("Hinweis fürs Lager (optional)")} className="col-span-6"><textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, 1000))} rows={2} className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-[13px] outline-none focus:border-border-strong" placeholder={t("z. B. Folie, Etikett auf Stirnseite")} /></Field>
              <Field label={t("Eigene Auftragsnummer (optional)")} className="col-span-6 sm:col-span-3"><Input value={reference} onChange={(e) => setReference(e.target.value.slice(0, 40))} placeholder={t("sonst automatisch B2B-…")} /></Field>
            </section>
          </div>
          <div className="px-6 py-3.5 border-t border-border flex items-center gap-3 bg-surface">
            <div className="min-w-0 flex-1 text-[12.5px] text-fg-muted">
              {lines.length ? <><b className="text-fg">{lines.length}</b> {lines.length === 1 ? t("Position") : t("Positionen")} · {fmtNumber(units)} {t("Stück")}</> : t("Noch keine Artikel")}
              {tried && !valid && <span className="block text-danger">{t("Bitte die markierten Felder ausfüllen.")}</span>}
            </div>
            <Dialog.Close asChild><Button variant="ghost">{t("Abbrechen")}</Button></Dialog.Close>
            <Button variant="primary" loading={create.isPending} onClick={() => { setTried(true); if (valid) create.mutate(); }}>{t("Lieferung anlegen")}</Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
